import { ATIVO_MERCADO, ATIVO_TIPO } from '@investirapri/financa.js/lib/enums';
import { validators } from 'investira.sdk';

export const ATIVO_DISPLAY_MODE = {
  bolsa: 'bolsa',
  fundo_aberto: 'fundo_aberto',
  fundo_listado: 'fundo_listado',
  fundo_fechado: 'fundo_fechado',
  titulo_publico: 'titulo_publico',
  titulo_privado: 'titulo_privado',
  titulo_bancario: 'titulo_bancario',
  previdencia: 'previdencia',
  cripto: 'cripto',
  derivativo: 'derivativo',
  outros: 'outros'
};

/**
 * Indica se o modo de exibição representa qualquer modalidade de fundo.
 */
export function isFundoDisplayMode(pDisplayMode) {
  return (
    pDisplayMode === ATIVO_DISPLAY_MODE.fundo_aberto ||
    pDisplayMode === ATIVO_DISPLAY_MODE.fundo_fechado ||
    pDisplayMode === ATIVO_DISPLAY_MODE.fundo_listado
  );
}

/**
 * Indica se o modo representa um fundo que não é negociado em bolsa.
 *
 * @param {string} pDisplayMode Modo de apresentação do ativo.
 * @returns {boolean}
 */
export function isFundoNaoListadoDisplayMode(pDisplayMode) {
  return isFundoDisplayMode(pDisplayMode) && pDisplayMode !== ATIVO_DISPLAY_MODE.fundo_listado;
}

/**
 * Indica se o modo representa uma modalidade de renda fixa.
 *
 * @param {string} pDisplayMode Modo de apresentação do ativo.
 * @returns {boolean}
 */
export function isRendaFixaDisplayMode(pDisplayMode) {
  return (
    pDisplayMode === ATIVO_DISPLAY_MODE.titulo_publico ||
    pDisplayMode === ATIVO_DISPLAY_MODE.titulo_privado ||
    pDisplayMode === ATIVO_DISPLAY_MODE.titulo_bancario
  );
}

/**
 * Indica se o modo representa um ativo com apresentação de negociação em bolsa.
 *
 * @param {string} pDisplayMode Modo de apresentação do ativo.
 * @returns {boolean}
 */
export function isNegociadoEmBolsaDisplayMode(pDisplayMode) {
  return (
    pDisplayMode === ATIVO_DISPLAY_MODE.bolsa || pDisplayMode === ATIVO_DISPLAY_MODE.fundo_listado
  );
}

const wFundoTipoListadoBolsa = [
  ATIVO_TIPO.CTII.id,
  ATIVO_TIPO.CTID.id,
  ATIVO_TIPO.ETF.id,
  ATIVO_TIPO.ETFRV.id,
  ATIVO_TIPO.ETFRF.id,
  ATIVO_TIPO.ETFRFPU.id,
  ATIVO_TIPO.BDRETFRV.id,
  ATIVO_TIPO.BDRETFRF.id,
  ATIVO_TIPO.BDRETFCM.id,
  ATIVO_TIPO.CTAG.id,
  ATIVO_TIPO.CTPI.id
].filter(Boolean);

const wDisplayModeByMercado = {
  [ATIVO_MERCADO.ACN.id]: ATIVO_DISPLAY_MODE.bolsa,
  [ATIVO_MERCADO.TPU.id]: ATIVO_DISPLAY_MODE.titulo_publico,
  [ATIVO_MERCADO.TPR.id]: ATIVO_DISPLAY_MODE.titulo_privado,
  [ATIVO_MERCADO.TBA.id]: ATIVO_DISPLAY_MODE.titulo_bancario,
  [ATIVO_MERCADO.PRV.id]: ATIVO_DISPLAY_MODE.previdencia,
  [ATIVO_MERCADO.CRP.id]: ATIVO_DISPLAY_MODE.cripto,
  [ATIVO_MERCADO.DRI.id]: ATIVO_DISPLAY_MODE.derivativo
};

/**
 * Classifica o ativo conforme a precedência de apresentação dos componentes.
 */
export function getAtivoDisplayMode(pAtivo = {}) {
  const xIsFundo = pAtivo.mercado === ATIVO_MERCADO.FND.id || !validators.isEmpty(pAtivo.fundo);

  // Ativos que não são fundos seguem a apresentação definida pelo mercado.
  if (!xIsFundo) {
    return wDisplayModeByMercado[pAtivo.mercado] || ATIVO_DISPLAY_MODE.outros;
  }

  const xIsTipoListado = wFundoTipoListadoBolsa.includes(pAtivo.ativo_tipo_id);
  const xHasTicker = !validators.isEmpty(pAtivo.ticker);

  // Tipos explicitamente negociados em bolsa prevalecem sobre a abertura do fundo.
  if (xIsTipoListado) {
    return ATIVO_DISPLAY_MODE.fundo_listado;
  }

  // Fundos fechados com ticker também são apresentados como listados.
  if (pAtivo?.fundo?.fechado === true && xHasTicker) {
    return ATIVO_DISPLAY_MODE.fundo_listado;
  }

  // Fundos fechados sem ticker mantêm o modo específico de fundo fechado.
  if (pAtivo?.fundo?.fechado === true) {
    return ATIVO_DISPLAY_MODE.fundo_fechado;
  }

  // Os demais fundos são apresentados como fundos abertos.
  return ATIVO_DISPLAY_MODE.fundo_aberto;
}
